import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import EBAPage from './pages/EBAPage';
import RulesPage from './pages/RulesPage';
import TimesheetsPage from './pages/TimesheetsPage';
import PayrollPage from './pages/PayrollPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SitesPage from './pages/SitesPage';
import DocketsPage from './pages/DocketsPage';
import SettingsPage from './pages/SettingsPage';
import EmployeesPage from './pages/EmployeesPage';
import Layout from './components/Layout';
import SessionTimeoutModal from './components/SessionTimeoutModal';

// API Setup
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Session expired event for global handling
let sessionExpiredHandler = null;
export const setSessionExpiredHandler = (handler) => {
  sessionExpiredHandler = handler;
};

// Axios interceptor to handle token expiry
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh the token
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const { access_token } = response.data;
          localStorage.setItem('token', access_token);
          API.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
          
          // Retry the original request
          return API(originalRequest);
        } catch (refreshError) {
          // Refresh failed - session expired
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete API.defaults.headers.common['Authorization'];
          
          if (sessionExpiredHandler) {
            sessionExpiredHandler();
          }
          
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionTimeout, setShowSessionTimeout] = useState(false);

  const handleSessionExpired = useCallback(() => {
    setUser(null);
    setShowSessionTimeout(true);
  }, []);

  const handleLoginFromTimeout = useCallback(() => {
    setShowSessionTimeout(false);
    // Navigate to login will happen automatically via ProtectedRoute
  }, []);

  useEffect(() => {
    // Set up session expired handler
    setSessionExpiredHandler(handleSessionExpired);
    
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
    
    return () => {
      setSessionExpiredHandler(null);
    };
  }, [handleSessionExpired]);

  const login = async (email, password, clientId) => {
    try {
      const response = await API.post('/auth/login', { 
        email, 
        password, 
        client_id: clientId 
      });
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      API.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userData);
      toast.success('Login successful');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      return false;
    }
  };

  const register = async (name, email, password, clientId) => {
    try {
      const response = await API.post('/auth/register', { 
        name,
        email, 
        password, 
        client_id: clientId 
      });
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      API.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userData);
      toast.success('Registration successful');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete API.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
      <SessionTimeoutModal 
        isOpen={showSessionTimeout} 
        onLogin={handleLoginFromTimeout} 
      />
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardPage />} />
            <Route path="eba" element={<EBAPage />} />
            <Route path="rules" element={<RulesPage />} />
            <Route path="sites" element={<SitesPage />} />
            <Route path="dockets" element={<DocketsPage />} />
            <Route path="timesheets" element={<TimesheetsPage />} />
            <Route path="payroll" element={<PayrollPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
